import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { observer } from "mobx-react";
import debounce from "lodash/debounce";
import Loader from "ui/Loader/Loader";
import SearchProductPreview from "common/SearchProductPreview/SearchProductPreview";
import { ControlledInputType } from "common/Header/components/Search/Search";
import Search from "assets/icons/search.svg";
import ChevronRightRound from "assets/icons/chevron-right-round.svg";
import CloseRound from "assets/icons/close-round.svg";
import { useStore } from "store";
import { setBodyOverflow } from "helpers/dom";
import { useBreakpoint, useUrl } from "helpers/hooks";
import { gtm } from "helpers/analytics";
import { cn } from "helpers/style";
import { ROUTE_CART, ROUTE_PRODUCT } from "constants/routes";
import SearchFrequencyRequestPreview from "./components/SearchFrequencyRequestPreview/SearchFrequencyRequestPreview";
import SearchSuggestions from "./components/SearchSuggestions/SearchSuggestions";
import SearchTap from "./components/SearchTap/SearchTap";
import SearchHistoryItem from "./components/SearchHistoryItem/SearchHistoryItem";
import Examples from "./components/Examples/Examples";
import styles from "./SearchInput.module.scss";

const LOCAL_STORAGE_HISTORY_SEARCH_KEY = "history_search_items";

type Props = {
  onChange?: (event: React.FocusEvent<HTMLInputElement>) => void;
  stickyIsOpen?: boolean;
  className?: string;
  classNameOverlay?: string;
  sticky?: boolean;
};

const SearchInput = forwardRef<ControlledInputType, Props>(
  ({ onChange, classNameOverlay, className, sticky, ...props }, ref) => {
    const { header, search } = useStore();
    const { getEntityLinkProps, getLinkProps } = useUrl();
    const breakpoint = useBreakpoint(true);
    const router = useRouter();
    const inputRef = useRef(null);
    const isMobile = breakpoint === "mobile";
    const hideToast =
      (isMobile && router.pathname === ROUTE_PRODUCT) ||
      router.pathname === ROUTE_CART;
    const { stickyIsOpen, ...other } = props;

    const [isLoading, setIsLoading] = useState(false);

    const [historyItems, setHistoryItems] = useState<string[]>(
      (typeof localStorage !== "undefined" &&
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_HISTORY_SEARCH_KEY))) ||
        []
    );

    useEffect(() => {
      !search.mounted && request();
      search.toggleMounted();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (stickyIsOpen) return;

      setBodyOverflow(search.showSuggestions ? "hidden" : "auto");
    }, [search.showSuggestions]);

    useImperativeHandle(ref, () => ({
      goToSearchPage: goToSearchPage,
      clear: onClearValue,
      input: inputRef.current,
    }));

    const goToSearchPage = (key: string) => {
      const searchValue = key || search.value;
      if (!searchValue) return;

      const { href, as } = getEntityLinkProps("search", {
        alias: searchValue,
      });

      setIsLoading(false);
      hideSuggestions();
      router.push(href, as);

      addHistorySearchItem(searchValue);
    };

    const onClearValue = async () => {
      onChange && onChange({ target: { value: "" } } as any);
      search.clear();
      setIsLoading(false);
    };

    const postfix = useMemo(() => {
      if (isLoading && !stickyIsOpen) {
        return <Loader small />;
      }
      return search.value ? (
        <button className={styles.searchInputClose} onClick={onClearValue}>
          <CloseRound />
        </button>
      ) : (
        <Link {...getLinkProps("/drugs/")}>
          <div
            className={styles.searchInputLink}
            onClick={() => header.closeAllMenus()}
          >
            <span>Поиск по алфавиту или заболеванию</span>
            <ChevronRightRound />
          </div>
        </Link>
      );
    }, [
      isLoading,
      search.value,
      getLinkProps,
      onClearValue,
      header,
      stickyIsOpen,
    ]);

    const updateSearchInput = (searchValue: string) => {
      search.cancelLoad();
      search.updateValue(searchValue);

      !search.showSuggestions && search.toggleShowSuggestions(true);

      setIsLoading(true);
      request();
    };

    const handleOnChange = useCallback(e => {
      onChange && onChange(e);
      updateSearchInput(e.target.value);
    }, []);

    const onTapClick = (tap: { relatedSearch: string; tap: string }) => {
      updateSearchInput(tap.relatedSearch);
    };

    const request = debounce(async () => {
      await search.fetch();

      if (search.value && search.showSuggestions) {
        gtm.autocompleteSuggestionShow(search.value, "Горизонтальный поиск");
      }
      setIsLoading(false);
    }, 500);

    const onWatch = useCallback(({ subject, isIntersecting, index }) => {
      if (isIntersecting) {
        gtm.watchProduct(subject, index + 1, "Поисковые подсказки");
      }
    }, []);

    const onClickProduct = ({ product, index }) => {
      gtm.onChoiceFromSearch(search.value || "", product.name);
      gtm.clickProduct(product, index + 1, "Поисковые подсказки");
    };

    const addHistorySearchItem = (newSearchItem: string) => {
      const newHistoryItems = [newSearchItem, ...historyItems];
      updateHistorySearchItems(newHistoryItems);
    };

    const updateHistorySearchItems = (newSearchItems: Array<string>) => {
      const updateSearchItems = Array.from(new Set(newSearchItems)).slice(0, 3);

      localStorage.setItem(
        LOCAL_STORAGE_HISTORY_SEARCH_KEY,
        JSON.stringify(updateSearchItems)
      );
      setHistoryItems(updateSearchItems);
    };

    const removeHistorySearchItem = (historySearchItem: string) => {
      const newHistoryItems = historyItems.filter(
        historyItem => historyItem !== historySearchItem
      );

      updateHistorySearchItems(newHistoryItems);
    };

    const histories = historyItems.map((name, idx) => {
      return (
        <SearchHistoryItem
          key={name + idx}
          name={name}
          onClick={() => {
            gtm.autocompleteHistoryClick(histories.length, idx + 1);
            gtm.onChoiceFromSearch(search.value || "", name);
            goToSearchPage(name);
          }}
          onRemove={() => {
            gtm.autocompleteHistoryRemove(histories.length, idx + 1);
            removeHistorySearchItem(name);
          }}
        />
      );
    });

    const formattingSuggestions = useMemo(() => {
      const products = (search.suggestions.products || [])
        .filter(product => {
          return product.price > 0;
        })
        .map((product, index) => {
          return (
            <SearchProductPreview
              onWatch={onWatch}
              index={index}
              key={product.id}
              product={product}
              analyticsMeta={{ position: index + 1, list: "Поиск" }}
              onGoToPage={() => {
                closeAllMenus();
                onClickProduct({ product, index });
                onClearValue();
              }}
              hideToast={hideToast}
            />
          );
        });

      const sts = (search.suggestions.sts || []).map((st, index) => {
        return (
          <SearchFrequencyRequestPreview
            sts={st}
            key={index}
            onFrequencyRequestClick={val => {
              gtm.onChoiceFromSearch(search.value || "", st.st);
              gtm.autocompleteOftenClick(index + 1);
              closeAllMenus();
              goToSearchPage(val);
            }}
          />
        );
      });

      const taps = (search.suggestions.taps || []).map((tap, index) => (
        <SearchTap
          key={index}
          tap={tap}
          onClick={tap => {
            gtm.autocompleteTapClick(
              search.suggestions.taps?.length,
              index + 1
            );
            onTapClick(tap);
          }}
        />
      ));

      return { products, sts, taps };
    }, [isLoading, search.list]);

    const onKeyPress = e => {
      gtm.autocompleteKeyPressed();
      if (!search.value.length) return;

      if (e.key === "Enter") {
        gtm.onChoiceFromSearch(search.value);
        closeAllMenus();
        goToSearchPage(search.value);
      }
    };

    const hideSuggestions = () => {
      inputRef.current.blur();
      search.cancelLoad();
      search.toggleShowSuggestions(false);
    };

    const handleFocus = () => {
      search.toggleShowSuggestions(true);
    };

    const onWheel = e => {
      e.stopPropagation();
      hideSuggestions();
    };

    const closeAllMenus = () => {
      header.closeAllMenus();
    };

    return (
      <div className={styles.searchInput}>
        <SearchSuggestions
          className={className}
          classNameList={cn([sticky && styles.searchInputSuggestionsSticky])}
          placeholder="Введите название лекарства"
          prefix={!isMobile && <Search />}
          postfix={!isMobile && postfix}
          value={search.value}
          products={formattingSuggestions.products}
          sts={formattingSuggestions.sts}
          taps={formattingSuggestions.taps}
          histories={histories}
          onChange={handleOnChange}
          onKeyPress={onKeyPress}
          onFocus={handleFocus}
          showSuggestions={!stickyIsOpen && search.showSuggestions}
          ref={inputRef}
          goToSearchPage={goToSearchPage}
          isLoading={isLoading}
          {...other}
        />
        {!sticky && <Examples onClick={addHistorySearchItem} />}
        {!stickyIsOpen && search.showSuggestions && (
          <div
            className={cn([styles.searchInputOverlay, classNameOverlay])}
            onWheel={onWheel}
            onScroll={onWheel}
            onClick={hideSuggestions}
          />
        )}
      </div>
    );
  }
);

export default observer(SearchInput);
